import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { Role, RoleHolders } from '../role-models';

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  /** The caller's role standing rarely changes; one request serves every page and dialog for a while. */
  private static readonly CALLER_TTL_MS = 5 * 60 * 1000;
  private callerCache: { at: number; caller$: Observable<{ isSystemAdmin: boolean }> } | null = null;

  constructor(private http:HttpClient) {
    // The cached answer belongs to one signed-in user: drop it on sign-out and when another user signs in.
    const oauth = inject(OAuthService, { optional: true });
    oauth?.userId$?.pipe(distinctUntilChanged()).subscribe(() => this.clearCaller());
    oauth?.loginChanged?.subscribe((loggedIn) => {
      if (!loggedIn) this.clearCaller();
    });
  }

  /** Forgets the cached caller standing (sign-out or user change). */
  clearCaller(): void {
    this.callerCache = null;
  }

  getRoles() {
    return this.http.get<Role[]>(`${environment.apiRoot}RolePermission/getrolepermissions`);
  }
  getUserRoles(userId: string | null) {
    return this.http.get<Role[]>(`${environment.apiRoot}RolePermission/getuserroles/${userId}`);
  }
  addRoleToUser(id: string, roleId: string) {
    return this.http.post<Role>(`${environment.apiRoot}RolePermission/addtorole/${id}/${roleId}`,null);
  }
  removeRoleFromUser(id: string, roleId: string) {
    return this.http.post<Role>(`${environment.apiRoot}RolePermission/removefromrole/${id}/${roleId}`,null);
  }
  addRole(role: Role) {
    return this.http.post<Role>(`${environment.apiRoot}RolePermission/addrole`,role);
  }
  deleteRole(roleId: string) {
    return this.http.delete<Role>(`${environment.apiRoot}RolePermission/deleterole/${roleId}`);
  }
  updateRole(
    roleId: string,
    patch: { name?: string; description?: string | null; isInternal?: boolean; isSystem?: boolean }
  ): Observable<Role> {
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(patch)) {
      if (v !== undefined) body[k] = v;
    }
    return this.http.put<Role>(`${environment.apiRoot}RolePermission/role/${roleId}`, body);
  }
  /** The users who hold a role (needs CanManageUserRoles; 404 for an unknown role). */
  getRoleUsers(roleId: string): Observable<RoleHolders> {
    return this.http.get<RoleHolders>(`${environment.apiRoot}RolePermission/role/${roleId}/users`);
  }
  getRoleEligibility(userId: string): Observable<{
    qualifiesForInternalRoles: boolean;
    companyName: string;
    internalVia: 'company' | 'representation' | null;
  }> {
    return this.http.get<{
      qualifiesForInternalRoles: boolean;
      companyName: string;
      internalVia: 'company' | 'representation' | null;
    }>(`${environment.apiRoot}RolePermission/user/${userId}/role-eligibility`);
  }
  /** Shared and cached for a few minutes; a failed request is not cached (shareReplay resets on error). */
  getCaller(): Observable<{ isSystemAdmin: boolean }> {
    const now = Date.now();
    if (!this.callerCache || now - this.callerCache.at > RoleService.CALLER_TTL_MS) {
      const caller$ = this.http
        .get<{ isSystemAdmin: boolean }>(`${environment.apiRoot}RolePermission/caller`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this.callerCache = { at: now, caller$ };
    }
    return this.callerCache.caller$;
  }
}
